import React, { useState } from "react";
import {
    Modal,
    Typography,
    Button,
    Select,
    Form,
    Checkbox,
    Switch,
    Tooltip,
    Row,
    Col,
} from "antd";
import { CloseOutlined, InfoCircleOutlined, DownOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;
const { Option } = Select;

interface PayCycleDetails {
    runningPayrollSince?: string;
    payrollCycleEndDate?: string;
}

interface Props {
    open: boolean;
    handleClose: () => void;
    payCycleDetails: PayCycleDetails;
    days: string[];
    explainDetails: string;
    remunerations: string;
}

const PayrollDialog: React.FC<Props> = ({
    open,
    handleClose,
    payCycleDetails,
    days,
    explainDetails,
    remunerations,
}) => {
    const [weeklyPay, setWeeklyPay] = useState(false);
    const [paidHoliday, setPaidHoliday] = useState(false);
    const [remunerationType, setRemunerationType] = useState<string | null>(null);
    const [payDay, setPayDay] = useState<string | null>(null);

    const handleSubmit = () => {
        console.log("Submit clicked");
        handleClose();
    };

    return (
        <Modal
            title={
                <Row justify="space-between" align="middle">
                    <Col>
                        <Title level={5}>Update Payroll Cycle Settings</Title>
                    </Col>
                    <Col>
                        <Button
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={handleClose}
                        />
                    </Col>
                </Row>
            }
            open={open}
            onCancel={handleClose}
            footer={null}
            width={1000}
        >
            <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                {/* Payroll Cycle Settings */}
                <div style={{ marginBottom: "1rem" }}>
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Title level={5}>Payroll Cycle Settings</Title>
                            <Text>Payroll has been processed through May 2024.</Text>
                        </Col>
                        <Col>
                            <Text
                                style={{ color: "#6A5ACD", cursor: "pointer" }}
                                onClick={() => console.log("Change Edit Date clicked")}
                            >
                                Change Edit Date
                            </Text>
                        </Col>
                    </Row>
                </div>

                {/* Running Payroll Info */}
                <div style={{ marginBottom: "1rem" }}>
                    <Title level={5}>Running payroll on Sortbox since</Title>
                    <Text strong>{payCycleDetails?.runningPayrollSince || "N/A"}</Text>
                </div>
                <div style={{ marginBottom: "1rem" }}>
                    <Title level={5}>Payroll Cycle End Date</Title>
                    <Text strong>{payCycleDetails?.payrollCycleEndDate || "N/A"}</Text>
                </div>

                {/* Payable Days Calculation */}
                <div style={{ marginBottom: "1rem" }}>
                    <Title level={5}>Payable Days Calculation</Title>
                    <Row align="middle" gutter={16}>
                        <Col>
                            <InfoCircleOutlined />
                        </Col>
                        <Col>
                            <Text>
                                If Pay Days in a month are less than Actual Days, or if Weekly
                                Offs/Holidays are unpaid, it will significantly impact salary
                                deductions due to LOP.{" "}
                                <Tooltip title={explainDetails} placement="right">
                                    <span style={{ color: "#6A5ACD", cursor: "pointer" }}>
                                        Explain how?
                                    </span>
                                </Tooltip>
                            </Text>
                        </Col>
                    </Row>
                </div>

                {/* Pay Days */}
                <div style={{ marginBottom: "1rem" }}>
                    <Title level={5}>Pay days in a month</Title>
                    <Form.Item>
                        <Select
                            placeholder="Select Pay Day"
                            value={payDay}
                            onChange={(value) => setPayDay(value)}
                            suffixIcon={<DownOutlined />}
                            style={{ width: "300px" }}
                        >
                            {days.map((day) => (
                                <Option key={day} value={day}>
                                    {day}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </div>

                {/* Weekly Off and Holiday Settings */}
                <div style={{ marginBottom: "1rem" }}>
                    <Form.Item label="Weekly Offs are paid">
                        <Switch
                            checked={weeklyPay}
                            onChange={(checked) => setWeeklyPay(checked)}
                        />
                    </Form.Item>
                    <Text>Employee will be paid for all the days in a month including the weekends.</Text>
                </div>
                <div style={{ marginBottom: "1rem" }}>
                    <Form.Item label="Holidays are paid">
                        <Switch
                            checked={paidHoliday}
                            onChange={(checked) => setPaidHoliday(checked)}
                        />
                    </Form.Item>
                    <Text>Employee will be paid for all the days in a month including the holidays.</Text>
                </div>

                {/* Remuneration Type */}
                <div style={{ marginBottom: "1rem" }}>
                    <Title level={5}>Remuneration Type</Title>
                    <Tooltip title={remunerations} placement="right">
                        <Text style={{ color: "#6A5ACD", cursor: "pointer" }}>
                            What is remuneration?
                        </Text>
                    </Tooltip>
                </div>
                <div style={{ marginBottom: "1rem" }}>
                    <Checkbox
                        checked={remunerationType === "Monthly"}
                        onChange={(e) => setRemunerationType(e.target.checked ? "Monthly" : null)}
                    >
                        Monthly
                    </Checkbox>
                    <Text>Monthly Remuneration is a monthly defined salary given to employees.</Text>
                </div>
                <div style={{ marginBottom: "1rem" }}>
                    <Checkbox
                        checked={remunerationType === "Daily"}
                        onChange={(e) => setRemunerationType(e.target.checked ? "Daily" : null)}
                    >
                        Daily
                    </Checkbox>
                    <Text>Daily Remuneration is a daily-based compensation for employee services.</Text>
                </div>

                {/* Buttons */}
                <Row justify="end" gutter={16}>
                    <Col>
                        <Button onClick={handleClose}>Cancel</Button>
                    </Col>
                    <Col>
                        <Button type="primary" onClick={handleSubmit}>
                            Update
                        </Button>
                    </Col>
                </Row>
            </div>
        </Modal>
    );
};

export default PayrollDialog;
